/* Packages */
import React, { useEffect, useMemo, useState } from "react";

/* Services */
import { useSelector } from "react-redux";
import { ingresseService } from "../_services";
import getCookieValue from "../_utils/getCookieValue";
import { resolveAutoSelectAlpha3 } from "../_utils/countryAutoSelect";
import { sortCountries } from "../_utils/sortCountries";

import { useTranslation } from "react-i18next";

/**
 * Countries pinned at the top of the list, in this exact order.
 * Remaining countries are sorted by numeric callingCode.
 */
const PRIORITY_ALPHA3 = ["BRA", "ESP", "URY", "USA", "MEX"];

/* Component Itself */
function InputCountryCode({ setCountryCode, disabled, required, ddi }) {
    const [loading, setLoading] = useState(false);
    const [selectedAlpha3, setSelectedAlpha3] = useState("");
    const [countryCodeList, setCountryCodeList] = useState([]);

    const { isFromIngresseDomain } = useSelector((state) => state.auth);
    const { t } = useTranslation();

    const location = getCookieValue("location");
    const locale = getCookieValue("locale");

    const sortedList = useMemo(
        () => sortCountries(countryCodeList, PRIORITY_ALPHA3),
        [countryCodeList]
    );

    useEffect(() => {
        (async () => {
            setLoading(true);
            const data = await ingresseService.api().get("/country");

            const { responseData } = data;
            if (typeof responseData !== "object" || !responseData.length) {
                return;
            }
            setCountryCodeList(responseData);
            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (!sortedList.length) return;

        if (ddi) {
            /**
             * Skip the sync when the currently selected alpha3 already
             * maps to the incoming ddi. Without this guard, countries
             * sharing a callingCode (e.g. USA/CAN with '+1') get rewritten
             * to the first match returned by `find`, overwriting the
             * user's explicit choice.
             */
            const currentMatch = sortedList.find(
                (c) => c.alpha3Code === selectedAlpha3
            );
            if (currentMatch && currentMatch.callingCode === ddi) {
                return;
            }

            const match = sortedList.find((c) => c.callingCode === ddi);
            if (match) {
                setSelectedAlpha3(match.alpha3Code);
            }
            return;
        }

        const alpha3 = resolveAutoSelectAlpha3({
            ddi,
            selectedAlpha3,
            locale,
            location,
        });
        if (!alpha3) {
            return;
        }

        // The country must exist in the list the API returned before it can be
        // selected: picking an alpha3 that is not there would clear the field
        // instead of filling it.
        const country = sortedList.find((c) => c.alpha3Code === alpha3);
        if (country) {
            setSelectedAlpha3(alpha3);
            setCountryCode(country.callingCode);
        }
        /**
         * Intentionally omitted deps:
         * - `selectedAlpha3` is read for the guards above; including it
         *   would cause a re-run loop with the setSelectedAlpha3 calls.
         * - `setCountryCode` must be stable from the parent (wrap with
         *   useCallback when consuming this component); we assume so to
         *   avoid running on every parent render.
         * - `locale` and `location` come from cookies read once per
         *   render — they don't change inside a session.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortedList, ddi]);

    const handleSelectChange = (alpha3Code) => {
        setSelectedAlpha3(alpha3Code);
        const match = sortedList.find((c) => c.alpha3Code === alpha3Code);
        setCountryCode(match ? match.callingCode : "");
    };

    /**
     * Render
     */
    return (
        <>
            <label htmlFor="countryCode" className="aph form__label">
                {t("AUTH.LABELS.DDI")}
                {required && <span className="text-red"> *</span>}
            </label>
            <select
                className={`aph form__control ${
                    !isFromIngresseDomain && "custom_mrt_input"
                }`}
                id="countryCode"
                onChange={(e) => handleSelectChange(e.target.value)}
                value={selectedAlpha3}
                disabled={disabled}
            >
                <option value="">
                    {t("AUTH.TEXT_SELECT.DEFAULT")}
                </option>
                {sortedList.map((countryCode) => {
                    const isSelected =
                        selectedAlpha3 === countryCode.alpha3Code;
                    const label = isSelected
                        ? countryCode.alpha3Code
                        : countryCode.name;

                    return (
                        <option
                            key={countryCode.alpha3Code}
                            value={countryCode.alpha3Code}
                        >
                            +{countryCode.callingCode} {label}
                        </option>
                    );
                })}
                {loading
                    ? Array.from({ length: 9 }).map((a, i) => (
                          <option key={"loading" + i} disabled value={""}>
                              {t("AUTH.LOADING_COUNTRIES")}
                          </option>
                      ))
                    : null}
            </select>
        </>
    );
}

/* Export Component */
export default InputCountryCode;
